import { Button, Checkbox } from "@blueprintjs/core";
import { IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";

import {
  ELab,
  HealthRequestPacksSearch,
  HealthTestRequestHealthPacksApi,
  HealthTestRequestPackGridDto,
} from "../../../../api";
import { CustomDataTable } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { HealthCharacteristicList } from "./HealthCharacteristicsList";

export interface IHealthPacksListProps {
  selectedIds: number[];
  blockedIds: number[];
  lab: ELab;
  dogId?: number;
  onSelectionUpdated: (selected: any) => void;
  disabled: boolean;
  healthTestRequestId: number;
}

export const HealthPacksList: React.FunctionComponent<IHealthPacksListProps> = ({
  selectedIds,
  lab,
  dogId = null,
  onSelectionUpdated,
  disabled,
  healthTestRequestId = null,
  blockedIds,
}) => {
  const api = useApiService(HealthTestRequestHealthPacksApi);
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();

  const [selectAll, setselectAll] = React.useState(false);
  const selectionList = React.useMemo(() => selectedIds ?? [], [selectedIds]);
  const closableIdsList = React.useMemo(() => [], []);

  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList?.length, ...closableIdsList);
    } else {
      selectionList.length = 0;
    }
    setselectAll(value);
    onSelectionUpdated(selectionList);
  }, [closableIdsList, onSelectionUpdated, selectAll, selectionList]);

  const columnCheckBox = React.useCallback(
    (row: HealthTestRequestPackGridDto) => {
      if (!disabled) {
        closableIdsList.push(row.healthPackId);
      }
      return (
        <Checkbox
          checked={selectionList.includes(row.healthPackId)}
          onChange={() => {
            if (selectionList.includes(row.healthPackId)) {
              const index = selectionList.findIndex((e) => e === row.healthPackId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.healthPackId);
            }
            onSelectionUpdated(selectionList);
          }}
          disabled={disabled || (blockedIds && blockedIds.includes(row.healthPackId))}
        />
      );
    },
    [blockedIds, closableIdsList, disabled, onSelectionUpdated, selectionList]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      healthTestRequestId == null && {
        header: () => <Checkbox onChange={toggleSelectAll} disabled={disabled} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: columnCheckBox,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "_toggle",
        autoFitContent: true,
        render: (item: HealthTestRequestPackGridDto, _, { subPanelOpened, toggleSubPanel }) => (
          <Button icon={subPanelOpened ? "minus" : "plus"} onClick={() => toggleSubPanel()} />
        ),
      },
      {
        header: () => t(ETLCodes.HealthPack),
        fieldName: "healthPack",
        render: (row: HealthTestRequestPackGridDto) => `[${row.healthPackCode}] ${row.healthPackName}`,
      },
    ],
    [columnCheckBox, disabled, healthTestRequestId, t, toggleSelectAll]
  );

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [50],
    pageSize: 50,
  });

  const searchFunction = React.useCallback(
    (sObj?: HealthRequestPacksSearch) => {
      return healthTestRequestId == null
        ? api.baseSearch(
            HealthRequestPacksSearch.fromJS({
              ...sObj,
              lab: lab,
              dogId: dogId,
              languageId: currentLanguage.languageId,
            })
          )
        : api.searchLinkedHealthPacks(
            HealthRequestPacksSearch.fromJS({
              ...sObj,
              healthTestRequestId: healthTestRequestId,
              languageId: currentLanguage.languageId,
            })
          );
    },
    [api, currentLanguage.languageId, dogId, healthTestRequestId, lab]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  React.useEffect(() => {
    if (lab) {
      search();
    }
  }, [lab, search]);

  return (
    <CustomDataTable
      tableState={tableState}
      loading={loading}
      columns={columns}
      keyFieldName="healthPackId"
      renderSubComponent={(item: HealthTestRequestPackGridDto) => (
        <HealthCharacteristicList data={item.healthTestPackCharacteristics} />
      )}
    />
  );
};
